/**
 * What one indicator is worth right now — spec §F5.9's popover content, extracted so it can
 * be shown in two places.
 *
 * "Big mono value + unit — **sum for count indicators, mean for rates over current scope** —
 * ▲/▼ delta vs previous year (green/red); scope line `{scope} · {year} · {domain}`; target
 * Donut gauge `{pct}% OF TARGET` (hidden if no target); 'Trend 2021–2026' Spark; 'By country
 * · year' BarsV (only when scope=ALL, country palette); buttons `Map this indicator` /
 * `Dashboard`. **Re-renders on country/year changes while open.**"
 *
 * ## Why it is a component of its own now (client review R7)
 *
 * > *"The space freed at the bottom of the left sidebar becomes an Analytics section for the
 * > current indicator+scope … reuse the IndicatorPopover content/components; the floating
 * > popover is no longer the default surface."*
 *
 * The numbers were only ever reachable through a floating panel that opened when you clicked
 * one of six round buttons on the right edge of the map. A reader who mapped an indicator
 * from the sidebar — the ordinary path — got a choropleth and no figure at all. So the same
 * content is mounted twice: pinned in the sidebar for whatever is **mapped** (`SidebarAnalytics`),
 * and floating for whatever the §F5.9 dock has **opened** (`IndicatorPopover`). One component,
 * because two copies of "the programme total for B.2.1" would eventually disagree.
 *
 * Four decisions worth stating, all of them unchanged by the move:
 *
 * 1. **The sum-vs-mean rule is the server's.** §F5.9's "sum for counts, mean for rates" is
 *    §E4 applied one level above admin 0, and the backend already owns it
 *    (`aggregate.programme_value`, exposed as `?level=programme` — see `lib/api.ts`).
 *    Aggregating the four country values in the browser would put a second, divergent
 *    implementation of the programme's headline numbers next to the dashboard's.
 * 2. **The delta comes from the same series the spark draws** — §E4's "value(year) −
 *    value(year−1)" — so the arrow can never contradict the slope above it. In 2021 there
 *    is no previous year and the delta is *omitted*: the mockup printed "▲ 0 vs —", which
 *    asserts "no change" about a comparison that was never made.
 * 3. **The target gauge is hidden, and the slot is real.** §E4: "'% of target': value /
 *    targets[year][scope] when targets present, else omit gauge (frontend hides)". §C's
 *    `indicators.targets` is not on the §D3 wire at all yet, so `targetPct` is `null` for
 *    every indicator today and the `<Donut/>` beneath it never renders — but the maths and
 *    the hide rule are here, not deferred (same call as NOTES #204's profile bar).
 * 4. **Re-rendering on country/year is TanStack Query's job, not an effect's.** Every
 *    number below hangs off a §F1 key containing the country and (where it matters) the
 *    year, so a time-bar tick or a geography change re-reads through the cache. That is also
 *    what makes the sidebar section "update live with year/mode/scope/country" for free.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { BarsV } from '../../components/charts/BarsV';
import { Donut } from '../../components/charts/Donut';
import { Spark } from '../../components/charts/Spark';
import {
  fetchIndicators,
  fetchProgrammeSeries,
  fetchProgrammeValue,
  fetchSeries,
  fetchUnits,
  fetchValues,
  modeFor,
  queryKeys,
  type UnitsFC,
} from '../../lib/api';
import { EMDASH, fmtNum } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { COUNTRY_COLORS, COUNTRY_ORDER } from '../../lib/palette';
import {
  COUNTRY_NAMES,
  effectiveLevels,
  scopeOwnLevel,
  selectYear,
  useAppStore,
} from '../../lib/state';
import { targetPct } from './dock';
import { HierarchyBreakdown } from './HierarchyBreakdown';
import { useIndicatorMeta } from './indicatorMeta';
import { SiblingBars } from './SiblingBars';

/** The unspent remainder of the gauge ring — the mockup's `#EEF1EC`. */
const GAUGE_REST = '#EEF1EC';

export interface IndicatorAnalyticsProps {
  indicatorId: string | null;
  /** Spark/bars width — the popover is 292 px wide inside, the sidebar section a little less. */
  width?: number;
  /**
   * §F5.9's "Map this indicator". Pointless in the sidebar section, whose subject *is* the
   * mapped indicator, so the caller decides rather than this file guessing from a variant.
   */
  showMapButton?: boolean;
  /**
   * Whether this panel is on screen — default `true`, which is the popover, whose whole
   * existence is being open. The sidebar section passes `sidebarOpen`, because a collapsed
   * sidebar stays mounted (see `SiblingBars`'s `enabled`). Only the sibling fan-out reads
   * it: the panel's own three queries are one request each and were always paid.
   */
  visible?: boolean;
}

export function IndicatorAnalytics({
  indicatorId,
  width = 292,
  showMapButton = true,
  visible = true,
}: IndicatorAnalyticsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setIndicator = useAppStore((s) => s.setIndicator);
  const country = useAppStore((s) => s.country);
  const scope = useAppStore((s) => s.scope);
  const year = useAppStore(selectYear);
  const cumulative = useAppStore((s) => s.cumulative);
  const open = Boolean(indicatorId);

  const { ind, domain, color } = useIndicatorMeta(indicatorId);
  // The same `['indicators']` entry the picker, the time bar and the legend all read — so
  // finding an indicator's siblings costs one `filter` and no request.
  const { data: catalogue } = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
  });
  // §F5.10's Cumulative checkbox — this reads the same way the map does, so the big number
  // and the choropleth under it are never two different questions.
  const mode = modeFor(cumulative, ind);

  /**
   * §F5.8: under a filter this is about the **filtered unit**, not the programme.
   *
   * "Affects the numbers" is most visible right here — the big mono figure is the one number
   * a reader quotes. `?parent=<scope>&level=<its own level>` returns exactly that unit's row
   * (the subtree filter is inclusive), so there is still no client-side aggregation and no
   * second endpoint: it is the same §E4 answer the choropleth is painting, narrowed to one.
   */
  const scoped = Boolean(scope);
  const { data: headline } = useQuery({
    queryKey: scope
      ? queryKeys.values(indicatorId ?? '', scopeOwnLevel(scope), year, country, mode, scope.unitId)
      : queryKeys.programmeValue(indicatorId ?? '', year, country, mode),
    queryFn: () =>
      scope
        ? fetchValues(indicatorId as string, scopeOwnLevel(scope), year, country, mode, scope.unitId)
        : fetchProgrammeValue(indicatorId as string, year, country, mode),
    enabled: open,
  });
  // The trend, and with it the previous year the delta compares against. Year-independent,
  // so a time-bar tick costs nothing. Under a filter it is §D3's per-unit series — the same
  // one §F5.7's profile spark draws, and therefore the same cache entry.
  const { data: series } = useQuery({
    queryKey: scope
      ? queryKeys.series(indicatorId ?? '', scope.unitId, mode)
      : queryKeys.programmeSeries(indicatorId ?? '', country, mode),
    queryFn: () =>
      scope
        ? fetchSeries(indicatorId as string, scope.unitId, mode)
        : fetchProgrammeSeries(indicatorId as string, country, mode),
    enabled: open,
  });
  // "By country" only exists as a comparison when more than one country is in scope — which
  // a §F5.8 filter never leaves, even for a cross-border watershed whose `country` is null.
  const showBars = country === 'ALL' && !scoped;
  const { data: byCountry } = useQuery({
    queryKey: queryKeys.values(indicatorId ?? '', 0, year, 'ALL', mode),
    queryFn: () => fetchValues(indicatorId as string, 0, year, 'ALL', mode),
    enabled: open && showBars,
  });
  // ISO3 per admin-0 unit id. The map already holds this exact cache entry
  // (`GeographyFocus` fetches it on every load), so this pays nothing for the mapping.
  const { data: adm0 } = useQuery<UnitsFC>({
    queryKey: queryKeys.units('admin', 0, 'ALL', 'z7'),
    queryFn: () => fetchUnits('admin', 0, 'ALL', 'z7'),
    enabled: open && showBars,
  });

  const value = headline?.values[0]?.value ?? null;
  const seriesYears = series?.years ?? [];
  const seriesValues = series?.values ?? [];
  const activeIdx = seriesYears.indexOf(year);
  const prevYear = activeIdx > 0 ? seriesYears[activeIdx - 1] : null;
  const prevValue = activeIdx > 0 ? (seriesValues[activeIdx - 1] ?? null) : null;
  // §E4's "value(year) − value(year − 1)", in whichever reading is on screen: against the
  // previous *annual* value in annual mode, and against the previous *running total* in
  // cumulative mode — where it is therefore the year's own increment, which is the same
  // number the annual mode prints. That is the arithmetic working, not a coincidence.
  const delta = value !== null && prevValue !== null ? value - prevValue : null;

  /** §G8's fixed country order, one bar per programme country. */
  const bars = useMemo(() => {
    const isoByUnit = new Map<string, string>();
    for (const f of adm0?.features ?? []) {
      if (f.properties.country) isoByUnit.set(f.properties.id, f.properties.country);
    }
    const byIso = new Map<string, number>();
    for (const row of byCountry?.values ?? []) {
      const iso = isoByUnit.get(row.unit_id);
      if (iso) byIso.set(iso, row.value);
    }
    return COUNTRY_ORDER.map((iso) => ({
      iso,
      value: byIso.has(iso) ? (byIso.get(iso) as number) : null,
      color: COUNTRY_COLORS[iso],
    }));
  }, [adm0, byCountry]);

  if (!open || !ind) return null;

  // §E4 / §C: no `indicators.targets` on the §D3 wire, so this is null for every indicator
  // and the gauge below stays hidden. The slot and the rule are live; only the data is not.
  const target: number | null = null;
  const pct = targetPct(value, target);

  // The sample behind a survey estimate. It goes on the scope line rather than beside the
  // big number because it qualifies *what the number is of*, which is what that line says —
  // and because "33.8 % n=3,580" read as one string invites the n to be mistaken for part of
  // the figure. Absent for every other kind of indicator, so nothing else gains a suffix.
  const sampleN = headline?.values[0]?.sample_n ?? null;

  const scopeLine = [
    // The filtered unit *is* the scope, and naming the country instead of it would leave the
    // one line that says what the number is of pointing at the wrong place.
    scope ? scope.name : country === 'ALL' ? t('dock.allCountries') : COUNTRY_NAMES[country],
    // The year, and — when the reader is accumulating — what the year *means* here. The
    // scope line is the one place this says what it is counting, and "2026" reading as a
    // whole-programme total without saying so is the confusion this removes.
    mode === 'cumulative' ? t('timebar.toYear', { year }) : String(year),
    // `fmtNum`, not `toLocaleString()`: the latter groups by the *browser's* locale, so a
    // reader on a French page with an English browser would get "n=3,580" beside "33,8 %".
    // §F12's rule is that the interface language is the locale (`lib/format.ts`).
    sampleN === null ? '' : t('analytics.sample', { n: fmtNum(sampleN, 0) }),
    domain?.short_name ?? '',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="ipo-body" data-ipobody={ind.id}>
      <div className="ipo-headline">
        <span className="ipo-val" data-ipoval="">
          {value === null ? EMDASH : fmtNum(value, ind.fmt)}
          {ind.unit ? <small> {ind.unit}</small> : null}
        </span>
        {delta === null ? null : (
          <span
            className={`ipo-delta ${delta >= 0 ? 'up' : 'down'}`}
            data-ipodelta={delta >= 0 ? 'up' : 'down'}
            style={{ color: delta >= 0 ? 'var(--open)' : 'var(--restricted)' }}
          >
            {delta >= 0 ? '▲' : '▼'} {fmtNum(Math.abs(delta), ind.fmt)}{' '}
            {t('dock.vsYear', { year: prevYear })}
          </span>
        )}
      </div>

      <div className="ipo-scope" data-iposcope="">
        {scopeLine}
      </div>

      {/* An em-dash where a number should be is the one thing this panel says without
          explaining, and for a survey estimate it is the ordinary state rather than a
          fault: the MELS baseline was estimated at country and watershed, so a region scope
          has nothing and always will. Only shown for `aggregation: 'none'` — for a roll-up
          indicator a missing number means the year is unreported, which is what the time bar
          and the legend already say. */}
      {value === null && ind.aggregation === 'none' ? (
        <div className="tbl-note" data-iponovalue="">
          {t('analytics.noValueAtScope')}
        </div>
      ) : null}

      {pct === null ? null : (
        <div className="gauge-wrap" data-ipogauge="">
          <Donut
            items={[
              { label: t('dock.achieved'), value: pct, color },
              { label: t('dock.toTarget'), value: 100 - pct, color: GAUGE_REST },
            ]}
            size={86}
            centerLabel={`${pct}%`}
            centerSub={t('dock.ofTarget')}
            fmt={0}
          />
          <div className="ipo-gauge-note">{t('dock.gaugeNote')}</div>
        </div>
      )}

      {/*
        **A series with no point to plot has no trend**, and the block goes rather than
        printing a heading over an empty chart.

        `/indicators/{id}/series?scope=programme` answers with the *programme's* year axis —
        2021 to 2026 — and a null per year for a series that reports none of them. For a
        contractual indicator that is a gap in reporting and the spark shows it; for a 2025
        survey estimate the survey did not compute at this scope it is permanent, and
        "Trend 2021–2026" over blank axes asserts a six-year range for a one-year measurement.
        That is the same lie `InsightsPanel` refuses when it drops a trend a dataset does not
        have, and since the 8 Sep directive ten rail chips lead straight here, so the state
        stopped being rare. The headline's own em-dash and its `data-iponovalue` sentence are
        what say there is no number; a chart saying it a second time, in axes, is not a
        second answer.
      */}
      {seriesValues.some((value) => value !== null && value !== undefined) ? (
        <>
          <h5 className="ipo-h5" data-ipotrend="">
            {t('dock.trend', {
              from: seriesYears[0] ?? 2021,
              to: seriesYears[seriesYears.length - 1] ?? 2026,
            })}
          </h5>
          <Spark
            series={seriesValues}
            w={width}
            h={62}
            color={color}
            unit={ind.unit}
            fmt={ind.fmt}
            activeIdx={activeIdx}
            labels={seriesYears}
            ariaLabel={t('dock.trendAria', { indicator: ind.name })}
          />
        </>
      ) : null}

      {/* P5(3): where this sub-indicator sits among the others from the same workbook sheet.
          Under the trend rather than above it — the trend is about *this* series over time,
          which is the panel's subject; the siblings are context for the current figure. */}
      <SiblingBars ind={ind} catalogue={catalogue ?? []} color={color} enabled={visible} />

      {/*
        §F5.9's own "By country · year" bars — kept, and kept **only** for the unfiltered
        programme view they were written for. §G8's fixed four colours and the ISO labels are
        the shape a reader already knows from the dashboard, so the programme extent keeps it
        verbatim; every rung below it is the general breakdown under this, which is the block
        the 9 Sep directive adds.
      */}
      {showBars ? (
        <>
          <h5 className="ipo-h5" data-ipobarstitle="">
            {t('dock.byCountry', { year })}
          </h5>
          <BarsV
            labels={bars.map((b) => b.iso)}
            values={bars.map((b) => b.value)}
            colors={bars.map((b) => b.color)}
            h={104}
            unit={ind.unit}
            fmt={ind.fmt}
            ariaLabel={t('dock.byCountryAria', { indicator: ind.name, year })}
          />
        </>
      ) : null}

      {/*
        The per-hierarchy breakdown — the donut and the bars for the rung *below* the current
        filter, for both indicator families. At the programme extent the bars above already
        are the country comparison, so only the ring is drawn there; under any filter this is
        the block that says where inside it the numbers come from. See `HierarchyBreakdown`.
      */}
      <HierarchyBreakdown
        ind={ind}
        value={value}
        color={color}
        width={width}
        enabled={visible}
        showBars={!showBars}
      />

      <div className="ipo-actions">
        {showMapButton ? (
          <button
            type="button"
            className="btn primary sm"
            data-ipomap=""
            // The dock button for a demo-only indicator is already dead, so this should be
            // unreachable for one — but `setDockInd` is public, and "map it" is the one
            // control here that would put simulated numbers on the map.
            disabled={!ind.active}
            title={!ind.active ? t('indicators.inactiveTitle') : undefined}
            onClick={() => setIndicator(ind.id, ind.has_watershed_values, effectiveLevels(ind))}
          >
            <Icon name="map" size={12} /> {t('dock.mapThis')}
          </button>
        ) : null}
        <button
          type="button"
          className="btn ghost sm"
          data-ipodash=""
          onClick={() => void navigate('/dashboard')}
        >
          <Icon name="chart" size={12} /> {t('dock.dashboard')}
        </button>
      </div>
    </div>
  );
}

export default IndicatorAnalytics;
